import Autocomplete from "@mui/material/Autocomplete";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import OutlinedInput from "@mui/material/OutlinedInput";
import React, { useMemo } from "react";
import {
  isEmptyString,
  isNullOrUndefined,
  isNumber,
  isString,
} from "base/utils";

const FormAutoComplete = ({
  fieldLabel,
  fieldType = "text",
  formClass,
  listOptions = [],
  placeholder,
  value: controlledValue,
  onChange,
  defaultValue,
  returnFullObject = false,
}) => {
  const isControlled = !isNullOrUndefined(controlledValue);
  const [internalValue, setInternalValue] = React.useState(defaultValue || "");

  const displayValue = isControlled ? controlledValue : internalValue;

  const options = useMemo(() => {
    if (!listOptions || listOptions.length === 0) return [];
    return listOptions.map((option) =>
      isString(option) || isNumber(option)
        ? String(option)
        : option.name || String(option),
    );
  }, [listOptions]);

  const getDisplayValue = () => {
    if (isNullOrUndefined(displayValue)) return "";
    if (displayValue?.name) return String(displayValue.name);
    return String(displayValue);
  };

  const processValue = (newValue) => {
    if (isNullOrUndefined(newValue) || newValue === "") return newValue;

    if (returnFullObject) {
      const foundOption = listOptions.find((option) => {
        if (isString(option) || isNumber(option)) {
          return String(option) === String(newValue);
        }
        return option.name === String(newValue);
      });
      return foundOption || newValue;
    }

    if (fieldType === "number" && !isEmptyString(newValue)) {
      const numValue = Number(newValue);
      return isNaN(numValue) ? newValue : numValue;
    }

    return newValue;
  };

  const handleChanges = (event, newValue) => {
    // Fires on dropdown selection (or clear)
    const processed = processValue(newValue);
    if (!isControlled) setInternalValue(processed);
    onChange?.(processed);
  };

  const handleInputChange = (event, newInputValue, reason) => {
    // Fires on every keystroke — only handle direct typing
    // "reset" fires after a selection and is already handled by handleChanges
    if (reason !== "input") return;

    const processed = processValue(newInputValue);
    if (!isControlled) setInternalValue(processed);
    onChange?.(processed);
  };

  return (
    <div className={formClass}>
      <Autocomplete
        disablePortal
        freeSolo
        value={getDisplayValue()}
        onChange={handleChanges}
        onInputChange={handleInputChange}
        options={options}
        renderInput={(params) => (
          <FormControl className="mb-3" fullWidth>
            {fieldLabel && <InputLabel shrink>{fieldLabel}</InputLabel>}
            <OutlinedInput
              {...params.InputProps}
              ref={params.InputProps.ref}
              inputProps={params.inputProps}
              placeholder={placeholder}
              label={fieldLabel}
              className="Outlined-Input"
              type={fieldType}
            />
          </FormControl>
        )}
      />
    </div>
  );
};

export default FormAutoComplete;